import {
    Button,
    Combobox,
    createTheme, Input,
    InputBase,
    Modal,
    NumberInput, Popover,
    Select, Switch, Tabs,
    TagsInput, Textarea,
    TextInput, Tooltip, UnstyledButton
} from "@mantine/core";
import cx from "clsx";
import classes from "./BiosTheme.module.css";
import commonClasses from "../../common.module.css"
import {DateTimePicker, TimeInput} from "@mantine/dates";
import biosThemeModalClasses from "./Modal.module.css";
import {Dropzone} from "@mantine/dropzone";

// Import BIOS branded fonts
import "./fonts.css";

// Typography constants for BIOS brand consistency
// BW Gradual: Main titles (all caps) - defined in fonts.css
// Beatrice: Subtitles and body text (sentence case)
// Verdana: Fallback when BIOS fonts unavailable
const FONT_FAMILY_SUBTITLE = '"Beatrice", Verdana, sans-serif';
const FONT_FAMILY_BODY = '"Beatrice", Verdana, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const FONT_FAMILY_MONOSPACE = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace';

export const theme = createTheme({
    /* -------------------------------------------------------------------------
       TYPOGRAPHY - BIOS Branded Fonts
       - BW Gradual: Main titles (H1) - ALL CAPS
       - Beatrice Large: Subtitles (H2-H3)
       - Beatrice Regular: Body text, pull quotes, and smaller headings
       - Emphasis: Underline or green-blue color (no bold/italics)
       ------------------------------------------------------------------------- */
    fontFamily: FONT_FAMILY_BODY,
    fontFamilyMonospace: FONT_FAMILY_MONOSPACE,
    headings: {
        // H1 uses BW Gradual (all caps), others use Beatrice
        fontFamily: FONT_FAMILY_SUBTITLE,
        fontWeight: "400", // Regular weight for Beatrice
        sizes: {
            h1: {
                fontSize: "2rem",
                lineHeight: "1.3",
            },
            h2: {
                fontSize: "1.625rem",
                lineHeight: "1.35",
            },
            h3: {
                fontSize: "1.375rem",
                lineHeight: "1.4",
            },
            h4: {
                fontSize: "1.125rem",
                lineHeight: "1.45",
            },
            h5: {
                fontSize: "1rem",
                lineHeight: "1.5",
            },
            h6: {
                fontSize: "0.875rem",
                lineHeight: "1.5",
            },
        },
    },

    /* -------------------------------------------------------------------------
       COLOR PALETTE - BIOS Brand Colors
       ------------------------------------------------------------------------- */
    colors: {
        primaryBlue: [
            "#e6e6ec",
            "#cccdd8",
            "#b3b4c5",
            "#999bb1",
            "#80829e",
            "#66698b",
            "#4d5077",
            "#333764",
            "#1a1e50",
            "#00053d",
        ],
        mediumBlue: [
            "#EBF4FF",
            "#D6E9FF",
            "#C2DEFF",
            "#ADD3FF",
            "#99C8FF",
            "#84BCFF",
            "#70B1FF",
            "#5BA6FF",
            "#479BFF",
            "#3290FF",
        ],
        lightBlue: [
            "#EBF9FC",
            "#D6F3FA",
            "#C2EDF7",
            "#ADE7F5",
            "#99E1F2",
            "#84DAEF",
            "#70D4ED",
            "#5BCEEA",
            "#47C8E8",
            "#32C2E5",
        ],
        greenBlue: [
            "#EBFDF7",
            "#D6FBF0",
            "#C2F9E8",
            "#ADF7E1",
            "#99F5D9",
            "#84F3D1",
            "#70F1CA",
            "#5BEFC2",
            "#47EDBB",
            "#32EBB3",
        ],
        purple: [
            "#F0F1FF",
            "#E1E2FF",
            "#D3D4FF",
            "#C4C6FF",
            "#B5B8FF",
            "#A6A9FF",
            "#979BFF",
            "#898DFF",
            "#7A7EFF",
            "#6B70FF",
        ],
        lilac: [
            "#F5F0FF",
            "#EBE2FF",
            "#E1D3FF",
            "#D7C5FF",
            "#CDB6FF",
            "#C2A7FF",
            "#B899FF",
            "#AE8AFF",
            "#A47CFF",
            "#9A6DFF",
        ],
        transparentMediumBlue: [
            "#EBF4FFA0",
            "#D6E9FFA0",
            "#C2DEFFA0",
            "#ADD3FFA0",
            "#99C8FFA0",
            "#84BCFFA0",
            "#70B1FFA0",
            "#5BA6FFA0",
            "#479BFFA0",
            "#3290FFA0",
        ]
    },
    components: {
        UnstyledButton: UnstyledButton.extend({}),
        Button: Button.extend({
            classNames: {
                 root: cx(classes.button)
            }
        }),
        TagsInput: TagsInput.extend({
            classNames: () => ({
                input: cx(classes.tagsInput),
                label: cx(classes.label),
            })
        }),
        Combobox: Combobox.extend({
            classNames: {
                dropdown: cx(classes.partiallyTransparentBG, commonClasses.border, commonClasses.sbRadius),
                option:cx(commonClasses.textCenter, commonClasses.hover),
                groupLabel:cx(commonClasses.subtleText,commonClasses.textBold),
            }
        }),
        InputBase: InputBase.extend({
            classNames: {
                input: cx(classes.input),
                label: cx(classes.label)
            }
        }),
        Select: Select.extend({
            classNames: {
                input: cx(commonClasses.mediumBlueBG0, commonClasses.textCenter),
                dropdown: cx(commonClasses.mediumBlueBG0, commonClasses.textCenter, classes.blur),
                option: cx(commonClasses.hover, commonClasses.textCenter),
            },
        }),
        NumberInput: NumberInput.extend({
            classNames: {
                input: cx(classes.input),
                label: cx(classes.label)
            },
        }),
        TimeInput: TimeInput.extend({
            classNames: {
                input: cx(classes.input, classes.timeInput),
                label: cx(classes.label),
                error: cx(classes.inputError),
            },
        }),
        TextInput: TextInput.extend({

            classNames: {
                input: cx(classes.input),
                label: cx(classes.label),
                error: cx(classes.inputError)
            },
        }),
        Textarea: Textarea.extend({
            classNames: {
                input: cx(classes.input),
                label: cx(classes.label),
                error: cx(classes.inputError)
            },
        }),
        Input: Input.extend({
            classNames: {
                input: cx(classes.input)
            },
        }),
        Modal: Modal.extend({
            defaultProps: {
                withinPortal: false
            },
            classNames: biosThemeModalClasses,
        }),
        Tooltip: Tooltip.extend({
            classNames: {
                tooltip: cx(classes.textColor, classes.partiallyTransparentBG, commonClasses.tgBorder, commonClasses.sbRadius),
                arrow:cx(classes.partiallyTransparentBG)
            }
        }),
        Popover: Popover.extend({
            defaultProps: {
                withinPortal: true,
            },
            classNames: {
                dropdown: cx(commonClasses.tgBorder, classes.partiallyTransparentBG),
                overlay: cx(classes.strongBlur),
                arrow: cx(classes.strongBlur, classes.partiallyTransparentBG),
            },
        }),
        Switch: Switch.extend({
            styles: (theme) => {
                return {
                    track: {
                        color: theme.colors.primaryBlue[0],
                        backgroundColor:theme.colors.primaryBlue[0],
                    },
                    label: {
                        color:theme.white
                    },
                    thumb: {
                        backgroundColor:theme.colors.primaryBlue[9],
                        borderColor:theme.colors.primaryBlue[9]
                    }
                }
            },
        }),
        Dropzone: Dropzone.extend({
            classNames: {
                root: cx(classes.dropZone, commonClasses.hover)
            }
        }),
        Tabs: Tabs.extend({
            classNames:{
                tab:cx(classes.tabs)
            }
        }),
        DateTimePicker: DateTimePicker.extend({
            classNames: {
                day: cx(classes.dtpDay)
            }
        })
    },

    /* -------------------------------------------------------------------------
       THEME CONFIGURATION
       ------------------------------------------------------------------------- */
    primaryColor: "primaryBlue",
    primaryShade: {light: 9, dark: 7},
    autoContrast: true,

    /* Default border radius for components */
    radius: {
        xs: "3px",
        sm: "5px",
        md: "8px",
        lg: "12px",
        xl: "16px",
    },
    defaultRadius: "sm",

    /* Focus ring for accessibility */
    focusRing: "auto",

    /* Respect user's reduced motion preference */
    respectReducedMotion: true,

    /* Cursor style for interactive elements */
    cursorType: "pointer",
})